import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuditAction, AuditActionListParams } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertCircle, CheckCircle2, Loader2, Pencil, RotateCcw, XCircle } from 'lucide-react';
import AuditActionDialog from './AuditActionDialog';
import CloseActionDialog from './CloseActionDialog';
import { ACTION_STATUS, PRIORITY_LABEL, daysFromToday } from './auditStatus';
import { rowOpens } from './rowClick';
import { useAuditActions, type SaveResult } from './useAuditActions';

export type ActionQuickFilter = 'mine' | 'open' | 'overdue' | 'closed';

const QUICK_FILTERS: Array<{ key: ActionQuickFilter; label: string }> = [
  { key: 'mine', label: 'My open' },
  { key: 'open', label: 'All open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'closed', label: 'Closed' },
];

interface AuditActionsTableProps {
  /** Fixed scope, e.g. one supplier or one audit. */
  scope?: Pick<AuditActionListParams, 'supplier' | 'audit'>;
  initialFilter?: ActionQuickFilter;
  hideSupplier?: boolean;
  /** Read-only (Helix staff): no status changes, edits or closing. */
  readOnly?: boolean;
  /** Highlight one action (deep link from a bell / calendar entry). */
  focusId?: number | null;
  /** Called after an action changes (the audit screen refreshes its counts and chips). */
  onChanged?: () => void;
  /** Show these instead of fetching (an archived audit's actions drop out of the actions list endpoint). */
  rows?: AuditAction[];
}

function paramsFor(filter: ActionQuickFilter): AuditActionListParams {
  if (filter === 'mine') return { owner: 'me' };
  if (filter === 'overdue') return { overdue: true };
  return {};
}

/**
 * Corrective actions: quick filters, inline Open / In progress, edit, and
 * close (completed or cancelled, with notes). Never shown to suppliers.
 */
export default function AuditActionsTable({
  scope, initialFilter = 'mine', hideSupplier = false, readOnly = false, focusId = null, onChanged, rows: given,
}: AuditActionsTableProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { formatDate } = useFormatters();
  const [filter, setFilter] = useState<ActionQuickFilter>(initialFilter);
  const data = useAuditActions({ ...scope, ...paramsFor(filter) }, !given);
  const [editing, setEditing] = useState<AuditAction | null>(null);
  const [closing, setClosing] = useState<{ action: AuditAction; status: 'completed' | 'cancelled' } | null>(null);
  /** A row click: on an audit's own page edit the action; elsewhere open its audit. */
  const openRow = (action: AuditAction) => {
    const open = action.status === 'open' || action.status === 'in_progress';
    if (scope?.audit) {
      if (open && !readOnly) setEditing(action);
      return;
    }
    navigate(`/audits/${action.audit}`);
  };

  const changed = async (work: Promise<SaveResult>): Promise<SaveResult> => {
    const result = await work;
    if (result === true) onChanged?.();
    return result;
  };

  // On one audit's own screen the Audit column would repeat itself.
  const showAudit = !scope?.audit;
  const rows = (given ?? data.actions).filter((a) => {
    const closed = a.status === 'completed' || a.status === 'cancelled';
    if (given && filter === 'mine' && a.owner !== currentUserId) return false;
    if (given && filter === 'overdue' && !a.is_overdue) return false;
    return filter === 'closed' ? closed : !closed;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {QUICK_FILTERS.map((f) => (
          <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'} onClick={() => setFilter(f.key)}>
            {f.label}
          </Button>
        ))}
      </div>
      {!given && data.loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading actions…
        </div>
      ) : !given && data.error ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <span className="flex items-center gap-2 text-sm text-destructive"><AlertCircle className="h-4 w-4" />{data.error}</span>
            <Button variant="outline" size="sm" onClick={() => void data.reload()}>Retry</Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {filter === 'closed' ? 'No closed actions yet.' : 'No open actions — nice.'}
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                {!hideSupplier && <TableHead>Supplier</TableHead>}
                {showAudit && <TableHead>Audit</TableHead>}
                <TableHead>Owner</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((action) => {
                const status = ACTION_STATUS[action.status];
                const open = action.status === 'open' || action.status === 'in_progress';
                const days = daysFromToday(action.due_date);
                return (
                  <TableRow key={action.id} tabIndex={0}
                    className={`cursor-pointer ${focusId === action.id ? 'bg-primary/5' : ''}`}
                    onClick={(e) => { if (rowOpens(e)) openRow(action); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) openRow(action); }}>
                    <TableCell className="max-w-md">
                      <button type="button" className="text-left hover:underline" onClick={() => navigate(`/audits/${action.audit}`)}>
                        {action.description}
                      </button>
                      {action.supplier_contact && (
                        <div className="text-xs text-muted-foreground">Contact: {action.supplier_contact}</div>
                      )}
                      {!open && (action.completion_notes || action.completed_at) && (
                        <div className="mt-1 rounded bg-muted px-2 py-1 text-xs" data-testid={`closure-${action.id}`}>
                          <span className="font-medium">{status.label}</span>
                          {action.completed_at && ` ${formatDate(action.completed_at)}`}
                          {action.completed_by_name && ` by ${action.completed_by_name}`}
                          {action.completion_notes && <span className="block whitespace-pre-wrap">{action.completion_notes}</span>}
                        </div>
                      )}
                    </TableCell>
                    {!hideSupplier && <TableCell>{action.supplier_name}</TableCell>}
                    {showAudit && (
                      <TableCell className="max-w-[14rem]">
                        <button type="button" className="text-left hover:underline" onClick={() => navigate(`/audits/${action.audit}`)}>
                          <span className="line-clamp-2">{action.audit_name || action.audit_template_name}</span>
                          <span className="block text-xs text-muted-foreground">{formatDate(action.audit_date)}</span>
                        </button>
                      </TableCell>
                    )}
                    <TableCell>{action.owner_name}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(action.due_date)}
                      {action.is_overdue && (
                        <Badge variant="outline" className="ml-2 bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">
                          Overdue {Math.abs(days)}d
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{PRIORITY_LABEL[action.priority]}</TableCell>
                    <TableCell>
                      {open && !readOnly ? (
                        <Select value={action.status}
                          onValueChange={(v) => {
                            // Completed / Cancelled close the action, which takes completion notes.
                            if (v === 'completed' || v === 'cancelled') setClosing({ action, status: v });
                            else void changed(data.setStatus(action.id, v as 'open' | 'in_progress'));
                          }}>
                          <SelectTrigger className="h-9 w-40" aria-label="Action status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open" className="py-2.5">Open</SelectItem>
                            <SelectItem value="in_progress" className="py-2.5">In progress</SelectItem>
                            <SelectItem value="completed" className="py-2.5">Completed</SelectItem>
                            <SelectItem value="cancelled" className="py-2.5">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={status.className}>{status.label}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!open && !readOnly && (
                        <Button size="sm" variant="outline" title="Reopen action"
                          onClick={() => void changed(data.setStatus(action.id, 'open'))}>
                          <RotateCcw className="h-4 w-4 mr-1" />Reopen
                        </Button>
                      )}
                      {open && !readOnly && (
                        <>
                          <Button size="icon" variant="ghost" title="Edit action" onClick={() => setEditing(action)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Close as done"
                            onClick={() => setClosing({ action, status: 'completed' })}>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Cancel action"
                            onClick={() => setClosing({ action, status: 'cancelled' })}>
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {editing && (
        <AuditActionDialog open onOpenChange={(o) => { if (!o) setEditing(null); }} customerId={editing.customer}
          auditId={editing.audit} action={editing}
          onSave={(payload) => changed(data.update(editing.id, {
            description: payload.description, supplier_contact: payload.supplier_contact,
            due_date: payload.due_date, priority: payload.priority,
            ...(payload.owner !== editing.owner ? { owner: payload.owner } : {}),
          }))} />
      )}
      <CloseActionDialog action={closing?.action ?? null} status={closing?.status ?? 'completed'}
        onOpenChange={(o) => { if (!o) setClosing(null); }}
        onClose={(id, status, notes) => changed(data.close(id, status, notes))} />
    </div>
  );
}
